// src/App.js
import { Analytics } from "@vercel/analytics/react";
import { MotionConfig } from "framer-motion";
import { Flag, LayoutDashboard, LineChart, MessageCircle, User, Users } from "lucide-react";
import React, { lazy, Suspense, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AccentThemeProvider, useAccentTheme } from "./contexts/AccentThemeContext";
import { MeasurementSystemProvider } from "./contexts/SpeedUnitContext";
import { useIsMobile } from "./hooks/useIsMobile";
import { cn } from "./lib/utils";
import { AppSettingsMenu } from "./components/common/AppSettingsMenu";
import BrandPreview from "./components/BrandPreview";
import { BrandMark } from "./components/common/BrandMark";
import { CarSpinner } from "./components/common/CarSpinner";
import { UploadJobMonitor } from "./components/common/UploadJobMonitor";

import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import { LegalDocument } from "./components/Auth/LegalDocument";
import { LegalGate } from "./components/Auth/LegalGate";
import { LegacyCompareRedirect } from "./components/NewDashboard/sessionAnalysisNavigation";
import { PageTransition } from "./components/PageTransition";
import { WelcomeOverlay } from "./components/WelcomeOverlay";
import { ComingSoon } from "./components/ui/ComingSoon";

const Career = lazy(() => import("./components/NewDashboard/Career").then(({ Career }) => ({ default: Career })));
const CompiledData = lazy(() => import("./components/NewDashboard/CompiledData").then(({ CompiledData }) => ({ default: CompiledData })));
const RawData = lazy(() => import("./components/NewDashboard/RawData").then(({ RawData }) => ({ default: RawData })));
const SessionAnalysis = lazy(() => import("./components/NewDashboard/SessionAnalysis").then(({ SessionAnalysis }) => ({ default: SessionAnalysis })));
const WidgetDashboard = lazy(() => import("./components/NewDashboard/WidgetDashboard").then(({ WidgetDashboard }) => ({ default: WidgetDashboard })));
const ChatBot = lazy(() => import("./components/ChatBot/ChatBot"));
const Social = lazy(() => import("./components/Social/Social").then(({ Social }) => ({ default: Social })));
const RaceAnalysis = lazy(() => import("./components/RaceAnalysis/RaceAnalysis").then(({ RaceAnalysis }) => ({ default: RaceAnalysis })));

function hasRaceAnalysisAccess(user) {
  const accountType = user?.account_type;
  if (!accountType) return true;
  return ["demo", "pro"].includes(String(accountType).toLowerCase());
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <CarSpinner className="h-40 w-40" />
      </div>
    );
  }

  return user ? <LegalGate>{children}</LegalGate> : <Navigate to="/Laptura/login" />;
}

function MainLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { accent } = useAccentTheme();
  const isMobile = useIsMobile();

  // The car sits on the native thumb, which is taller than the silhouette, so
  // --scroll-p pins the artwork to the top of the thumb at the start of the
  // page and the bottom at the end — otherwise the car never reaches either
  // rail. data-scroll-dir flips the nose. Both update on the next frame so a
  // burst of scroll events costs one write, not one each.
  const lastScrollY = useRef(0);
  const scrollFrame = useRef(null);
  const syncScrollbarCar = (el) => {
    const y = el.scrollTop;
    const range = el.scrollHeight - el.clientHeight;
    const p = range > 0 ? Math.min(1, Math.max(0, y / range)) : 0;
    document.documentElement.style.setProperty('--scroll-p', String(p));
    if (y > lastScrollY.current + 1) document.documentElement.dataset.scrollDir = 'down';
    else if (y < lastScrollY.current - 1) document.documentElement.dataset.scrollDir = 'up';
    lastScrollY.current = y;
  };
  const handleScrollDirection = (event) => {
    const el = event.currentTarget;
    if (scrollFrame.current !== null) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = null;
      syncScrollbarCar(el);
    });
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--scroll-p', '0');
    document.documentElement.dataset.scrollDir = 'down';
    lastScrollY.current = 0;
  }, [location.pathname]);

  useEffect(() => {
    const onWindowScroll = () => {
      if (scrollFrame.current !== null) return;
      scrollFrame.current = requestAnimationFrame(() => {
        scrollFrame.current = null;
        syncScrollbarCar(document.scrollingElement || document.documentElement);
      });
    };
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, []);

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/Laptura/dashboard' },
    { id: 'session-analysis', icon: Flag, label: 'Session Analysis', shortLabel: 'Sessions', path: '/Laptura/session-analysis' },
    ...(hasRaceAnalysisAccess(user)
      ? [{ id: 'race-analysis', icon: LineChart, label: 'Race Analysis', shortLabel: 'Race', path: '/Laptura/race-analysis' }]
      : []),
    { id: 'chat', icon: MessageCircle, label: 'Lappy - AI Coach', shortLabel: 'Lappy', path: '/Laptura/chat' },
    { id: 'career', icon: User, label: 'Career', path: '/Laptura/career' },
    { id: 'social', icon: Users, label: 'Social', path: '/Laptura/social' },
  ];

  const isActive = (path) => {
    if (path === '/Laptura/dashboard') {
      return location.pathname === path;
    }
    if (path === '/Laptura/career') {
      // Career tab should be active for career, profile, history, and history sub-routes
      return location.pathname === path || 
             location.pathname === '/Laptura/profile' || 
             location.pathname === '/Laptura/history' ||
             location.pathname.startsWith('/Laptura/history/');
    }
    if (path === '/Laptura/session-analysis') {
      // Session Analysis tab should be active for session-analysis and analysis
      return location.pathname === path || 
             location.pathname === '/Laptura/analysis' ||
             location.pathname.startsWith(path + '/');
    }
    if (path === '/Laptura/race-analysis') {
      return location.pathname === path ||
             location.pathname === '/race-analysis' ||
             location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Chat + Dashboard are fixed viewports: outer main must not scroll.
  const isChatRoute = location.pathname === '/Laptura/chat' || location.pathname.startsWith('/Laptura/chat/');
  const isDashboardRoute = location.pathname === '/Laptura/dashboard';
  const isFixedViewport = isChatRoute || isDashboardRoute;

  return (
    <div className={cn(
      "bg-lap-bg text-lap-text flex flex-col md:flex-row",
      isFixedViewport
        ? "h-[100dvh] overflow-hidden"
        : "min-h-screen md:h-screen md:overflow-hidden"
    )}>
      {/* Desktop Sidebar - Fixed */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 glass border-y-0 border-l-0 border-r border-r-lap-hairline flex-col z-30" style={{ overscrollBehavior: 'none' }}>
        <div className="h-16 border-b border-lap-hairline flex items-center justify-between px-6">
          <BrandMark />
          {(window.location.hostname === 'localhost' || process.env.NODE_ENV === 'development') && (
            <span className="px-2 py-0.5 text-[10px] font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded">
              Local
            </span>
          )}
          {(window.location.hostname.includes('vercel.app') && window.location.hostname !== 'www.laptura.app' && window.location.hostname !== 'laptura.app') && (
            <span className="px-2 py-0.5 text-[10px] font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded">
              Preview
            </span>
          )}
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.filter((item) => !item.comingSoon).map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'instant' });
                        const mainContent = document.querySelector('main');
                        if (mainContent) {
                          mainContent.scrollTo({ top: 0, behavior: 'instant' });
                        }
                      }, 0);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-full transition-colors",
                      isActive(item.path)
                        ? "bg-lap-accent text-white"
                        : "text-lap-dim hover:text-lap-text hover:bg-lap-raised"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-lap-hairline">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div className="min-w-0 text-sm text-lap-muted">
              <p className="font-medium text-lap-text truncate">{user?.username || user?.name}</p>
              <p className="text-xs text-lap-dim truncate">{user?.email}</p>
            </div>
            <AppSettingsMenu align="left" />
          </div>
          <div className="text-xs text-lap-dim">
            <p>Version 2.0.0</p>
            <p className="mt-1">© 2026 Laptura</p>
          </div>
        </div>
      </aside>

      {/* Mobile Header - Fixed (compact 40px) */}
        <div className="md:hidden fixed top-0 left-0 right-0 h-10 glass border-x-0 border-t-0 border-b border-b-lap-hairline flex items-center justify-between px-3 z-[65]" style={{ overscrollBehavior: 'none' }}>
        <div className="flex items-center min-w-0">
          <BrandMark compact />
          {(window.location.hostname === 'localhost' || process.env.NODE_ENV === 'development') && (
            <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded">
              Local
            </span>
          )}
          {(window.location.hostname.includes('vercel.app') && window.location.hostname !== 'www.laptura.app' && window.location.hostname !== 'laptura.app') && (
            <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded">
              Preview
            </span>
          )}
        </div>
        <AppSettingsMenu />
      </div>

      {/* Main Content - Offset for fixed sidebar on desktop, fixed mobile header on mobile */}
      {/* 
        Z-index hierarchy:
        - z-30: Mobile header, desktop sidebar, base page headers
        - z-[60]: Main content area (scrollbar visible, content scrolls behind headers)
        - z-[65]: Page-specific fixed headers (Career, SessionAnalysis) - above main content
        - z-[70]: Mobile bottom navigation - always on top and clickable
        - z-[100]: WelcomeOverlay and other global overlays
        
        Page-specific headers (z-[65]) are above main (z-[60]) so content scrolls behind them properly.
        Mobile bottom nav (z-[70]) is highest to ensure it's always clickable.
        Any new global overlays should use z-index > 70, or be rendered outside of main.
      */}
      <main 
        onScroll={handleScrollDirection}
        className={cn(
          "flex-1 overflow-x-hidden pt-10 md:pt-0 md:pb-0 md:ml-64 relative z-[60]",
          isFixedViewport
            ? "overflow-hidden flex flex-col min-h-0 md:h-full"
            : "overflow-y-scroll md:overflow-y-scroll"
        )}
        style={{ 
          overscrollBehavior: 'contain',
          scrollbarGutter: isFixedViewport ? 'auto' : 'stable',
          // Mobile bottom nav: pad all non-chat pages (incl. fixed dashboard)
          paddingBottom: isChatRoute || !isMobile ? '0' : 'calc(4.5rem + env(safe-area-inset-bottom, 0px))'
        }}
      >
        {/* Keyed remount gives the entrance fade on every route change.
            No AnimatePresence: exit choreography could block navigation when
            the outgoing page contained its own animation trees (stuck
            "wait" left the app blank until a hard refresh). */}
        <PageTransition
          key={`${location.pathname}:${accent}`}
          className={isFixedViewport ? 'h-full min-h-0 flex flex-col' : ''}
        >
          <Suspense
            fallback={(
              <div className="flex min-h-[50vh] flex-1 items-center justify-center">
                <CarSpinner className="h-32 w-32" />
              </div>
            )}
          >
            {children}
          </Suspense>
        </PageTransition>
      </main>

      {/* Mobile Bottom Navigation - floating glass capsule (echoes iOS tab bar).
          Same z-[70] slot and safe-area handling as before; the capsule floats
          above the safe area instead of painting a solid bar under it. */}
      <nav
        className="md:hidden fixed left-3 right-3 glass rounded-full z-[70]"
        style={{ overscrollBehavior: 'none', bottom: 'calc(max(env(safe-area-inset-bottom, 0px), 8px) + 4px)' }}
      >
        <ul className="flex items-center justify-around h-14">
          {navItems.filter((item) => !item.desktopOnly && !item.comingSoon).map((item) => {
            const Icon = item.icon;

            return (
              <React.Fragment key={item.id}>
                <li className="flex-1">
                  <button
                    onClick={() => {
                      navigate(item.path);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={cn(
                      "w-full h-full flex flex-col items-center justify-center gap-0.5 transition-colors",
                      isActive(item.path)
                        ? "text-lap-accent-bright"
                        : "text-lap-dim"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[11px] leading-tight font-medium whitespace-nowrap">{item.shortLabel || item.label}</span>
                  </button>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

function AppContent() {
  const { user, showWelcome, welcomeName, welcomeRunId, dismissWelcome } = useAuth();
  const canAccessRaceAnalysis = hasRaceAnalysisAccess(user);

  return (
    <>
      <UploadJobMonitor />
      {showWelcome && (
        <WelcomeOverlay
          key={welcomeRunId}
          username={user?.username || user?.name || welcomeName}
          signedIn={Boolean(user)}
          onComplete={dismissWelcome}
        />
      )}
    <Routes>
      {/* Public Routes */}
      <Route path="/Laptura/login" element={<Login />} />
      <Route path="/Laptura/register" element={<Register />} />
      <Route path="/Laptura/legal" element={<LegalDocument />} />
      {process.env.NODE_ENV === 'development' && (
        <Route path="/Laptura/brand-preview" element={<BrandPreview />} />
      )}

      {/* Protected Routes - New Consolidated Routes */}
      <Route
        path="/Laptura/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WidgetDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/Laptura/session-analysis"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SessionAnalysis />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/Laptura/chat"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChatBot />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/Laptura/race-analysis"
        element={
          <ProtectedRoute>
            <MainLayout>
              {canAccessRaceAnalysis ? (
                <RaceAnalysis />
              ) : (
                <ComingSoon
                  title="Race Analysis Access"
                  description="Race Analysis is currently available for demo/pro accounts."
                  icon={LineChart}
                />
              )}
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/race-analysis" element={<Navigate to="/Laptura/race-analysis" replace />} />
      <Route path="/race-analysis/*" element={<Navigate to="/Laptura/race-analysis" replace />} />
      <Route
        path="/Laptura/compare"
        element={<LegacyCompareRedirect />}
      />
      <Route
        path="/Laptura/social"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Social />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/Laptura/career"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Career />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Legacy Routes - Redirect to new routes for backward compatibility */}
      <Route
        path="/Laptura/analysis"
        element={<Navigate to="/Laptura/session-analysis" replace />}
      />
      <Route
        path="/Laptura/profile"
        element={<Navigate to="/Laptura/career" replace />}
      />
      <Route
        path="/Laptura/history"
        element={<Navigate to="/Laptura/career" replace />}
      />
      
      {/* Track detail view route */}
      <Route
        path="/Laptura/history/track/:trackName"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Career />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* History sub-routes - still work from Career page */}
      <Route
        path="/Laptura/history/:sessionId/raw-data"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RawData />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/Laptura/history/:sessionId/compiled-data"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CompiledData />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/Laptura/login" />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <MeasurementSystemProvider>
        <AccentThemeProvider>
          <Router>
            {/* reducedMotion="user" honors prefers-reduced-motion for every framer animation */}
            <MotionConfig reducedMotion="user">
              <div className="App">
                <Toaster
                  position="top-right"
                  containerStyle={{
                    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                    left: 12,
                    right: 12,
                  }}
                  toastOptions={{
                    duration: 4000,
                    style: {
                      maxWidth: 'min(360px, calc(100vw - 24px))',
                      padding: '9px 12px',
                      fontSize: 13,
                      color: 'rgb(var(--lap-text))',
                      background: 'rgb(var(--lap-card) / 0.96)',
                      border: '1px solid rgb(var(--lap-hairline))',
                      borderRadius: 12,
                      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.38)',
                      backdropFilter: 'blur(16px)',
                    },
                    loading: {
                      icon: <CarSpinner className="h-7 w-7" />,
                    },
                    success: {
                      iconTheme: {
                        primary: 'rgb(var(--lap-gain))',
                        secondary: 'rgb(var(--lap-card))',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: 'rgb(var(--lap-loss))',
                        secondary: 'rgb(var(--lap-card))',
                      },
                    },
                  }}
                />
                <AppContent />
                <Analytics />
              </div>
            </MotionConfig>
          </Router>
        </AccentThemeProvider>
      </MeasurementSystemProvider>
    </AuthProvider>
  );
}

export default App;
